import { useState, useEffect, useCallback } from 'react'
import { api } from '../lib/api'
import { useAuth } from '../hooks/useAuth'
import toast from 'react-hot-toast'
import { Modal, Input, Select, Empty, MarketBadge } from '../components/UI'
import { MARKETS } from '../lib/constants'
import { usePersistentSort } from '../hooks/usePersistentSort'
import { useTableFilter, FilterButton } from '../components/TableFilter'

const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—'
const fmtMoney = (a, c) => a != null ? `${c||''} ${parseFloat(a).toFixed(2)}` : '—'

function daysUntil(d) {
  if (!d) return null
  return Math.ceil((new Date(d).getTime() - Date.now()) / 86400000)
}

export default function ShopifyOrders() {
  const { user } = useAuth()
  const isOwner = user?.role === 'owner'
  const perms = user?.permissions || {}
  const canEdit = isOwner || perms?.shopifyorders === true || perms?.shopifyorders?.edit === true

  const [tab, setTab] = useState('orders')
  const [orders, setOrders] = useState([])
  const [stores, setStores] = useState([])
  const [loading, setLoading] = useState(true)
  const [showArchived, setShowArchived] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [storeModal, setStoreModal] = useState(null)
  const [form, setForm] = useState({})
  const [saving, setSaving] = useState(false)
  const { sortCol, setSortCol, sortDir, setSortDir } = usePersistentSort('shopifyorders', 'order_date')
  const storeNames = [...new Set(orders.map(o=>o.store_name).filter(Boolean))]
  const ORDER_FILTERS = [
    { key:'market', label:'Market', options: MARKETS.map(m=>({value:m,label:m})) },
    { key:'store_name', label:'Store', options: storeNames.map(n=>({value:n,label:n})) },
    { key:'_chargeback', label:'Chargeback', options:[{value:'yes',label:'Has chargeback'},{value:'no',label:'No chargeback'}] },
    { key:'financial_status', label:'Payment', options:[{value:'paid',label:'Paid'},{value:'pending',label:'Pending'},{value:'refunded',label:'Refunded'},{value:'partially_refunded',label:'Partially refunded'},{value:'voided',label:'Voided'}] },
    { key:'fulfillment_status', label:'Fulfillment', options:[{value:'fulfilled',label:'Fulfilled'},{value:'unfulfilled',label:'Unfulfilled'},{value:'partial',label:'Partial'}] },
  ]
  const orderFilter = useTableFilter('shopifyorders', ORDER_FILTERS)
  const toggleSort = (col) => { if (sortCol===col) setSortDir(d=>d==='asc'?'desc':'asc'); else { setSortCol(col); setSortDir('asc') } }
  const sortArrow = (col) => sortCol===col ? (sortDir==='asc'?' ▲':' ▼') : ''

  const loadOrders = useCallback(() => {
    setLoading(true)
    api.shopOrders(showArchived).then(setOrders).catch(e => toast.error(e.message)).finally(() => setLoading(false))
  }, [showArchived])
  const loadStores = useCallback(() => { api.shopStores().then(setStores).catch(() => {}) }, [])

  useEffect(() => { loadOrders() }, [loadOrders])
  useEffect(() => { loadStores() }, [loadStores])

  const syncNow = async () => {
    setSyncing(true)
    try { const r = await api.shopSyncAll(); toast.success('Sync complete'); loadOrders(); loadStores() }
    catch (e) { toast.error(e.message) }
    setSyncing(false)
  }

  const openAddStore = () => { setForm({ status:'Active', market:'' }); setStoreModal('add') }
  const openEditStore = (s) => { setForm({ ...s, client_id:'', client_secret:'' }); setStoreModal(s) }
  const setF = k => e => setForm(f => ({ ...f, [k]: e.target.value }))

  const saveStore = async () => {
    setSaving(true)
    try {
      if (storeModal === 'add') await api.shopCreateStore(form)
      else await api.shopUpdateStore(storeModal.id, form)
      toast.success('Saved'); setStoreModal(null); loadStores()
    } catch (e) { toast.error(e.message) }
    setSaving(false)
  }
  const delStore = async (s) => {
    if (!window.confirm(`Delete store "${s.store_name}" and all its orders?`)) return
    try { await api.shopDeleteStore(s.id); toast.success('Deleted'); loadStores(); loadOrders() }
    catch (e) { toast.error(e.message) }
  }
  const testStore = async (s) => {
    const t = toast.loading('Testing…')
    try { const r = await api.shopTestStore(s.id); toast.success(`Connected: ${r.name||'ok'} (${r.currency||''})`, { id:t }) }
    catch (e) { toast.error(e.message, { id:t }) }
  }
  const syncOne = async (s) => {
    const t = toast.loading('Syncing…')
    try { const r = await api.shopSyncStore(s.id); toast.success(`${r.orders} orders, ${r.disputes} disputes`, { id:t }); loadOrders() }
    catch (e) { toast.error(e.message, { id:t }) }
  }

  const chargebackCell = (r) => {
    if (!r.chargeback_status) return <span style={{color:'var(--text3)'}}>—</span>
    const d = daysUntil(r.dispute_due_by)
    const urgent = d != null && d <= 3
    return (
      <div style={{display:'flex',flexDirection:'column',gap:2}}>
        <span style={{fontSize:11,fontWeight:700,color:'#EF4444'}}>⚠ {r.chargeback_status}</span>
        {r.chargeback_reason && <span style={{fontSize:10,color:'var(--text3)'}}>{r.chargeback_reason}</span>}
        {r.dispute_due_by && <span style={{fontSize:10,fontWeight:700,color:urgent?'#EF4444':'#F59E0B'}}>
          Submit by {fmtDate(r.dispute_due_by)}{d != null ? ` (${d}d)` : ''}
        </span>}
      </div>
    )
  }

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%'}}>
      <div className="toolbar">
        <div className="toolbar-left">
          <span className="toolbar-title">🛒 Shopify Orders</span>
          <div style={{display:'flex',gap:4,marginLeft:8}}>
            <button onClick={()=>setTab('orders')} style={{background:tab==='orders'?'var(--accent)':'var(--bg3)',color:tab==='orders'?'#fff':'var(--text3)',border:'1px solid var(--border2)',borderRadius:6,padding:'4px 12px',fontSize:12,fontWeight:600,cursor:'pointer'}}>📦 Orders</button>
            {canEdit && <button onClick={()=>setTab('stores')} style={{background:tab==='stores'?'var(--accent)':'var(--bg3)',color:tab==='stores'?'#fff':'var(--text3)',border:'1px solid var(--border2)',borderRadius:6,padding:'4px 12px',fontSize:12,fontWeight:600,cursor:'pointer'}}>🏪 Stores</button>}
          </div>
          <span className="toolbar-sub">{tab==='orders' ? `${orders.length} orders` : `${stores.length} stores`}</span>
        </div>
        <div style={{display:'flex',gap:8}}>
          {tab==='orders' && <FilterButton groups={ORDER_FILTERS} {...orderFilter} />}
          {tab==='orders' && <button className="btn btn-secondary" onClick={()=>setShowArchived(v=>!v)}>{showArchived?'📁 Archived':'📂 Active'}</button>}
          {canEdit && tab==='orders' && <button className="btn btn-primary" onClick={syncNow} disabled={syncing}>{syncing?'Syncing…':'🔄 Sync now'}</button>}
          {canEdit && tab==='stores' && <button className="btn btn-primary" onClick={openAddStore}>+ Add Store</button>}
        </div>
      </div>

      {tab==='orders' && <div className="table-wrap" style={{flex:1,overflow:'auto'}}>
        {loading ? <Empty icon="⏳" title="Loading…" />
          : orders.length===0 ? <Empty icon="📦" title="No orders" sub={canEdit?'Add a store and click Sync now.':'No orders synced yet.'} />
          : <table className="data-table">
            <thead><tr>
              {[['store_name','Store'],['market','Market'],['order_number','Order #'],['order_date','Date'],['customer_name','Customer'],['product_summary','Products'],['total_amount','Amount'],['financial_status','Payment'],['fulfillment_status','Fulfillment'],['tracking_number','Tracking #'],['chargeback_status','Chargeback']].map(([col,label])=>(
                <th key={col} onClick={()=>toggleSort(col)} style={{cursor:'pointer',userSelect:'none',whiteSpace:'nowrap'}}>{label}{sortArrow(col)}</th>
              ))}
            </tr></thead>
            <tbody>{[...orders].filter(r => orderFilter.matches(r, { _chargeback: x => x.chargeback_status ? 'yes':'no' })).sort((a,b)=>{
              let av=a[sortCol]??'', bv=b[sortCol]??''
              if (sortCol==='total_amount') { av=parseFloat(av)||0; bv=parseFloat(bv)||0; return sortDir==='asc'?av-bv:bv-av }
              if (sortCol==='order_date') { av=av?new Date(av).getTime():0; bv=bv?new Date(bv).getTime():0; return sortDir==='asc'?av-bv:bv-av }
              av=String(av); bv=String(bv)
              return sortDir==='asc'?av.localeCompare(bv):bv.localeCompare(av)
            }).map(r => (
              <tr key={r.id} style={r.chargeback_status?{background:'rgba(239,68,68,0.05)'}:undefined}>
                <td style={{fontWeight:600,fontSize:12}}>{r.store_name}</td>
                <td>{r.market ? <MarketBadge market={r.market} /> : '—'}</td>
                <td style={{fontFamily:'DM Mono,monospace',fontSize:12}}>{r.order_number}</td>
                <td style={{fontSize:11,whiteSpace:'nowrap'}}>{fmtDate(r.order_date)}</td>
                <td style={{fontSize:12}}>{r.customer_name||'—'}</td>
                <td style={{fontSize:11,maxWidth:220}}>{r.product_summary||'—'}</td>
                <td style={{fontSize:12,fontWeight:600,whiteSpace:'nowrap'}}>{fmtMoney(r.total_amount, r.currency)}</td>
                <td><span style={{fontSize:11,color:r.financial_status==='paid'?'var(--accent3)':'#F59E0B'}}>{r.financial_status||'—'}</span></td>
                <td><span style={{fontSize:11,color:r.fulfillment_status==='fulfilled'?'var(--accent3)':'var(--text3)'}}>{r.fulfillment_status||'unfulfilled'}</span></td>
                <td style={{fontSize:11,fontFamily:'DM Mono,monospace'}}>{r.tracking_number||'—'}</td>
                <td>{chargebackCell(r)}</td>
              </tr>
            ))}</tbody>
          </table>}
      </div>}

      {tab==='stores' && canEdit && <div className="table-wrap" style={{flex:1,overflow:'auto'}}>
        <table className="data-table">
          <thead><tr><th>Store Name</th><th>Domain</th><th>Market</th><th>Status</th><th>Credentials</th><th>Last Sync</th><th style={{textAlign:'center'}}>Actions</th></tr></thead>
          <tbody>{stores.map(s => (
            <tr key={s.id}>
              <td style={{fontWeight:600}}>{s.store_name}</td>
              <td style={{fontSize:11,fontFamily:'DM Mono,monospace'}}>{s.shop_domain}</td>
              <td>{s.market ? <MarketBadge market={s.market} /> : '—'}</td>
              <td><span style={{fontSize:11,color:s.status==='Active'?'var(--accent3)':'var(--text3)'}}>{s.status}</span></td>
              <td>{s.has_creds ? <span style={{fontSize:11,color:'var(--accent3)'}}>✓ Set</span> : <span style={{fontSize:11,color:'#EF4444'}}>Missing</span>}</td>
              <td style={{fontSize:10}}>{s.last_sync_at ? <>{new Date(s.last_sync_at).toLocaleString()}<div style={{color:'var(--text3)'}}>{s.last_sync_status}</div></> : '—'}</td>
              <td>
                <div style={{display:'flex',gap:4,justifyContent:'center',flexWrap:'wrap'}}>
                  <button className="btn btn-secondary" style={{padding:'4px 8px',fontSize:11}} onClick={()=>testStore(s)}>Test</button>
                  <button className="btn btn-secondary" style={{padding:'4px 8px',fontSize:11}} onClick={()=>syncOne(s)}>Sync</button>
                  <button className="btn btn-secondary" style={{padding:'4px 8px',fontSize:11}} onClick={()=>openEditStore(s)}>✏️</button>
                  <button className="btn btn-danger" style={{padding:'4px 8px',fontSize:11}} onClick={()=>delStore(s)}>🗑</button>
                </div>
              </td>
            </tr>
          ))}</tbody>
        </table>
      </div>}

      {storeModal && <Modal title={storeModal==='add'?'Add Store':'Edit Store'} onClose={()=>setStoreModal(null)}
        footer={<><button className="btn btn-ghost" onClick={()=>setStoreModal(null)}>Cancel</button>
          <button className="btn btn-primary" onClick={saveStore} disabled={saving}>{saving?'Saving…':'Save'}</button></>}>
        <Input label="Store Name" value={form.store_name||''} onChange={setF('store_name')} />
        <Input label="Shop Domain (e.g. mystore.myshopify.com)" value={form.shop_domain||''} onChange={setF('shop_domain')} />
        <Select label="Market" options={['', ...MARKETS]} value={form.market||''} onChange={setF('market')} />
        <Select label="Status" options={['Active','Paused']} value={form.status||'Active'} onChange={setF('status')} />
        <Input label={storeModal==='add'?'Client ID':'Client ID (leave blank to keep existing)'} value={form.client_id||''} onChange={setF('client_id')} />
        <Input label={storeModal==='add'?'Client Secret':'Client Secret (leave blank to keep existing)'} type="password" value={form.client_secret||''} onChange={setF('client_secret')} />
      </Modal>}
    </div>
  )
}
